import { useState } from "react";
import { Link } from "react-router";
import { Camera, Clock, Images, MapPin, UserRound } from "lucide-react";
import type { PhotoMatchBrief, SearchHit } from "../../../shared/contracts.js";
import { photoUrl, screenshotUrl } from "../../api.js";
import { PlateList, SnippetText, SourceBadge } from "../../components/ListingBits.js";
import { Badge } from "../../components/ui/Badge.js";
import { Card } from "../../components/ui/Card.js";
import { Tooltip } from "../../components/ui/Tooltip.js";
import { fmtCLP, fmtDate, fmtDecimal, fmtRelative, plural, titleCase } from "../../lib/format.js";
import { MATCH_FIELD_LABELS, SCORE_PART_LABELS } from "../../lib/labels.js";
import { photoMatchBadge } from "../../lib/photos.js";

/**
 * Miniatura con proporción fija: la grilla no salta mientras cargan las imágenes.
 * Pantallazo del aviso; si no hay (o falla), la primera foto guardada.
 */
function Thumb({ hit }: { hit: SearchHit }) {
  const sources = [
    ...(hit.thumbSnapshotId ? [{ src: screenshotUrl(hit.thumbSnapshotId, "thumb"), photo: false }] : []),
    ...(hit.photoThumbSnapshotId ? [{ src: photoUrl(hit.photoThumbSnapshotId, 0, "thumb"), photo: true }] : []),
  ];
  const [attempt, setAttempt] = useState(0);
  const current = sources[attempt];
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-t-lg border-b border-border bg-surface-2">
      {current ? (
        <img
          key={current.src}
          src={current.src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setAttempt((a) => a + 1)}
          className={current.photo ? "size-full object-cover object-center" : "size-full object-cover object-top"}
        />
      ) : (
        <div className="grid size-full place-items-center text-subtle">
          <span className="flex flex-col items-center gap-1 text-[12px]">
            <Camera className="size-6" aria-hidden="true" />
            Sin imágenes
          </span>
        </div>
      )}
      <div className="absolute top-2 left-2">
        <SourceBadge source={hit.source} />
      </div>
    </div>
  );
}

function scoreExplanation(hit: SearchHit): string {
  const parts = Object.entries(hit.scoreParts)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${SCORE_PART_LABELS[k] ?? k} ${fmtDecimal(v)}`);
  return parts.length > 0 ? `Puntaje ${fmtDecimal(hit.score)}: ${parts.join(" · ")}` : `Puntaje ${fmtDecimal(hit.score)}`;
}

/** "12 días después del siniestro" / "3 días antes" / "el mismo día". */
function incidentText(days: number): string {
  if (days === 0) return "El mismo día del siniestro";
  const n = Math.abs(days);
  return `${n} ${n === 1 ? "día" : "días"} ${days < 0 ? "antes" : "después"} del siniestro`;
}

export function ResultCard({ hit, incidentDays = null, photoMatches }: {
  hit: SearchHit;
  incidentDays?: number | null;
  /** Otros avisos con la misma foto (se cargan aparte, para todo el caso). */
  photoMatches?: PhotoMatchBrief[];
}) {
  const photoBadge = photoMatchBadge(photoMatches, hit.plates.filter((p) => p.strength === "strong").map((p) => p.value));
  const vehicle = [titleCase(hit.make), titleCase(hit.model), hit.year ? String(hit.year) : ""].filter(Boolean).join(" ");
  const place = [hit.comuna, hit.region].filter(Boolean).join(", ");
  // El título enlaza al detalle y un pseudo-elemento extiende el clic a toda la
  // tarjeta, sin anidar enlaces ni romper la navegación con teclado.
  return (
    <Card className="group relative flex flex-col transition-shadow hover:shadow-pop focus-within:ring-2 focus-within:ring-ring/40">
      <Thumb hit={hit} />
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-[14px] leading-snug font-semibold text-fg">
            <Link
              to={`/avisos/${encodeURIComponent(hit.listingId)}`}
              className="outline-none after:absolute after:inset-0 after:rounded-lg hover:underline focus-visible:underline"
            >
              {hit.title || "Aviso sin título"}
            </Link>
          </h3>
          <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-[13px] text-muted">
            {vehicle && <span className="font-medium text-fg">{vehicle}</span>}
            {hit.color && <span>{titleCase(hit.color)}</span>}
          </p>
        </div>

        <p className="text-[15px] font-semibold text-fg tabular-nums">{fmtCLP(hit.price)}</p>

        {incidentDays !== null && (
          <p>
            <Badge tone={incidentDays < 0 ? "warning" : "neutral"}
              title={incidentDays < 0 ? "Publicado antes del siniestro: revise si el vehículo ya estaba dañado o a la venta" : undefined}>
              {incidentText(incidentDays)}
            </Badge>
          </p>
        )}

        {photoBadge && (
          <p>
            <Badge tone={photoBadge.nearPlate ? "warning" : "accent"} icon={<Images />} title={photoBadge.title}>
              {photoBadge.nearPlate ? `${photoBadge.text}, patente casi igual` : photoBadge.text}
            </Badge>
          </p>
        )}

        {hit.plates.length > 0 && (
          // Por encima del enlace extendido: el globo de procedencia debe poder enfocarse.
          <div className="relative z-10">
            <PlateList plates={hit.plates} max={4} />
          </div>
        )}

        {hit.personNames.length > 0 && (
          <p className="flex items-start gap-1.5 text-[13px] text-fg">
            <UserRound className="mt-0.5 size-3.5 shrink-0 text-subtle" aria-hidden="true" />
            <span className="sr-only">Personas: </span>
            <span className="line-clamp-2">{hit.personNames.join(" · ")}</span>
          </p>
        )}

        <SnippetText segments={hit.snippet} className="line-clamp-4" />

        <div className="mt-auto space-y-2 pt-1">
          {hit.matched.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="sr-only">Coincide en:</span>
              {hit.matched.map((m) => (
                <Badge key={m} tone={m === "plate_near" ? "warning" : "success"}
                  title={m === "plate_near" ? "La patente del aviso difiere en un carácter de la buscada (posible error de lectura o de tipeo)" : undefined}>
                  {MATCH_FIELD_LABELS[m] ?? m}
                </Badge>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-subtle">
            <span className="inline-flex items-center gap-1" title={`Primera vez: ${fmtDate(hit.firstSeenAt)}`}>
              <Clock className="size-3.5" aria-hidden="true" />
              Visto por última vez {fmtRelative(hit.lastSeenAt)}
            </span>
            {place && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{place}</span>
              </span>
            )}
            {hit.snapshotCount > 1 && <span>{plural(hit.snapshotCount, "captura", "capturas")}</span>}
            <span className="relative z-10 ml-auto">
              <Tooltip content={scoreExplanation(hit)} align="end">
                <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] text-muted tabular-nums">
                  <span className="sr-only">Puntaje </span>
                  {fmtDecimal(hit.score)}
                </span>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function ResultCardSkeleton() {
  return (
    <Card aria-hidden="true" className="flex flex-col">
      <div className="aspect-[4/3] w-full animate-pulse rounded-t-lg bg-surface-3" />
      <div className="space-y-2 p-3">
        <div className="h-4 w-4/5 animate-pulse rounded bg-surface-3" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-surface-3" />
        <div className="h-5 w-1/3 animate-pulse rounded bg-surface-3" />
        <div className="h-3 w-full animate-pulse rounded bg-surface-3" />
        <div className="h-3 w-5/6 animate-pulse rounded bg-surface-3" />
      </div>
    </Card>
  );
}
